/**
 * Tracker Page
 *
 * Top-level page for the Relationship Tracker feature at /tracker.
 * Clean layout with stats, calendar, and AI discussion tab.
 */

import { format, startOfMonth, endOfMonth } from "date-fns";
import { Layout } from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarDays, MessageCircle } from "lucide-react";
import { EntryCalendar } from "@/components/tracker/EntryCalendar";
import { TrackerStats } from "@/components/tracker/TrackerStats";
import { TrackerChat } from "@/components/tracker/TrackerChat";

export function TrackerPage() {
  const now = new Date();
  const monthStart = format(startOfMonth(now), "yyyy-MM-dd");
  const monthEnd = format(endOfMonth(now), "yyyy-MM-dd");

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-2xl md:max-w-5xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">
            Relationship Tracker
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tap any day to log how you feel. Track patterns over time.
          </p>
        </div>

        {/* Stats */}
        <div className="mb-6">
          <TrackerStats startDate={monthStart} endDate={monthEnd} />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="calendar">
          <TabsList className="grid w-full grid-cols-2 h-10">
            <TabsTrigger
              value="calendar"
              className="gap-2 text-sm data-[state=active]:shadow-sm"
            >
              <CalendarDays className="h-4 w-4" />
              Calendar
            </TabsTrigger>
            <TabsTrigger
              value="discuss"
              className="gap-2 text-sm data-[state=active]:shadow-sm"
            >
              <MessageCircle className="h-4 w-4" />
              Discuss
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="mt-5">
            <div className="rounded-xl border bg-card p-4 sm:p-6">
              <EntryCalendar />
            </div>
          </TabsContent>

          <TabsContent value="discuss" className="mt-5">
            <TrackerChat />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
